import { paginateSupabase } from '@/lib/supabase-paginate';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";


type DisplayCurrency = "ETB" | "USD";

type Args = {
  bankAccountIds: string[];
  dateFrom: string; // YYYY-MM-DD
  dateTo: string; // YYYY-MM-DD
  targetCurrency?: DisplayCurrency | null;
};

export type OpeningBalanceRow = {
  opening_balance: number | null;
  opening_balance_ts: string | null;
  currency: string | null;
  tx_id?: string | null;
};

export type ConsolidatedOpeningBalancesRow = Record<string, number>;

export type BankAccountMeta = {
  id: string;
  name: string;
  currency: string | null;
  exchange_rate_type: string | null;
};

export type ConvertedOpeningBalanceDay = {
  amount: number;
  currency: DisplayCurrency;
  isPartial: boolean;

  includedAccountIds: string[];
  includedAccounts: AccountBalanceDetail[];

  missingFxAccountIds: string[];
  missingFxAccounts: AccountBalanceDetail[];
  missingFxAccountsCount: number;
  missingFxAmountByCurrency: Record<string, number>;

  missingSourceAccountIds: string[];
  missingSourceAccounts: AccountBalanceDetail[];
  missingSourceAccountsCount: number;
  missingSourceAmountByCurrency: Record<string, number>;
};

export type OpeningBalancesMatrix = {
  dateKeys: string[];
  byAccount: Map<string, Map<string, OpeningBalanceRow>>;
  consolidatedByCurrency: Map<string, ConsolidatedOpeningBalancesRow>;
  accountMeta: Map<string, BankAccountMeta>;
  converted?: Map<string, ConvertedOpeningBalanceDay>;
};

export type AccountBalanceDetail = {
  accountId: string;
  accountName: string;
  currency: string | null;
  amount: number | null;
  reason?: "included" | "missing_source" | "missing_fx";
  rateType?: string | null;

  appliedRate?: number | null;
  convertedAmount?: number | null;
  targetCurrency?: DisplayCurrency | null;
  rateDate?: string | null;
};

type ExchangeRateRow = {
  currency_pair: string;
  rate_type: string;
  rate: number | string;
  effective_date: string;
  bank_account_id: string | null;
};

type ManualBalanceRow = {
  id: string;
  bank_account_id: string;
  balance_date: string;
  closing_balance: number | string | null;
  currency: string | null;
  created_at: string | null;
};

type TxClosingRow = {
  bal: number | null;
  ccy: string | null;
  ts: string | null;
  tx_id: string | null;
};

type ManualClosingRow = {
  bal: number | null;
  ccy: string | null;
  balance_date: string;
  created_at: string | null;
  snapshot_id: string | null;
};

const toNum = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const normType = (v: unknown) => String(v ?? "").trim().toLowerCase();
const pad2 = (n: number) => String(n).padStart(2, "0");

function buildDateKeys(dateFrom: string, dateTo: string): string[] {
  const start = new Date(`${dateFrom}T00:00:00Z`);
  const end = new Date(`${dateTo}T00:00:00Z`);
  const keys: string[] = [];

  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    keys.push(
      `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`
    );
  }

  return keys;
}

function addToCurrencyBucket(
  bucket: Record<string, number>,
  currency: string | null | undefined,
  amount: number | null | undefined
) {
  if (!currency || amount == null) return;
  bucket[currency] = (bucket[currency] ?? 0) + amount;
}

function buildRateLookupKey(params: {
  currencyPair: string;
  rateType: string;
  bankAccountId?: string | null;
}) {
  return `${params.currencyPair}__${params.rateType}__${params.bankAccountId ?? "global"}`;
}

function compareIsoDate(a: string | null | undefined, b: string | null | undefined) {
  const aa = a ?? "";
  const bb = b ?? "";
  if (aa < bb) return -1;
  if (aa > bb) return 1;
  return 0;
}

function compareIsoTs(a: string | null | undefined, b: string | null | undefined) {
  const aa = a ?? "";
  const bb = b ?? "";
  if (aa < bb) return -1;
  if (aa > bb) return 1;
  return 0;
}

export function useOpeningBalancesMatrixRange(args: Args) {
  const { bankAccountIds, dateFrom, dateTo, targetCurrency = null } = args;

  return useQuery({
    queryKey: [
      "opening_balance_tx_matrix",
      [...bankAccountIds].sort(),
      dateFrom,
      dateTo,
      targetCurrency,
    ],
    enabled: bankAccountIds.length > 0 && !!dateFrom && !!dateTo,

    queryFn: async (): Promise<OpeningBalancesMatrix> => {
      const dateKeys = buildDateKeys(dateFrom, dateTo);
      const dateKeySet = new Set(dateKeys);

      const tsFrom = `${dateFrom}T00:00:00+00:00`;

      const toPlus1 = new Date(`${dateTo}T00:00:00Z`);
      toPlus1.setUTCDate(toPlus1.getUTCDate() + 1);
      const tsToPlus1 = toPlus1.toISOString();

      // ---------- Fetch aggregated per-day first/last tx + seeds + manual + fx ----------
      const [dailyRes, accountsRes, manualRows, fxRows, txSeedRes, manualSeedRes] =
        await Promise.all([
          // In-range: aggregated first/last tx per (account, day) via RPC
          (supabase as any).rpc("get_bank_tx_daily_first_last", {
            p_account_ids: bankAccountIds,
            p_from: tsFrom,
            p_to: tsToPlus1,
          }),

          supabase
            .from("bank_accounts")
            .select("id, name, currency, exchange_rate_type")
            .in("id", bankAccountIds),

          // In-range manual balances (small — one row per acc/day; hard cap)
          (async () => {
            const { data, error } = await supabase
              .from("bank_account_balances")
              .select("id, bank_account_id, balance_date, closing_balance, currency, created_at")
              .in("bank_account_id", bankAccountIds)
              .gte("balance_date", dateFrom)
              .lte("balance_date", dateTo)
              .not("closing_balance", "is", null)
              .order("bank_account_id", { ascending: true })
              .order("balance_date", { ascending: true })
              .order("created_at", { ascending: true })
              .limit(10000);
            if (error) throw error;
            return (data ?? []) as ManualBalanceRow[];
          })(),

          // Exchange rates — only when we need FX conversion
          targetCurrency
            ? (async () => {
                const data = await paginateSupabase<ExchangeRateRow>((from, to) =>
                  supabase
                    .from("exchange_rates")
                    .select("currency_pair, rate_type, rate, effective_date, bank_account_id")
                    .lte("effective_date", dateTo)
                    .order("effective_date", { ascending: false })
                    .range(from, to) as any
                );
                return data;
              })()
            : Promise.resolve([] as ExchangeRateRow[]),

          // Seed: last bank transaction with end_balance strictly before dateFrom, per account
          (supabase as any).rpc("get_last_bank_tx_before", {
            p_account_ids: bankAccountIds,
            p_before: tsFrom,
          }),

          // Seed: last manual balance snapshot strictly before dateFrom, per account
          (supabase as any).rpc("get_last_manual_balance_before", {
            p_account_ids: bankAccountIds,
            p_before_date: dateFrom,
          }),
        ]);

      if (accountsRes.error) throw accountsRes.error;
      if (dailyRes.error) throw dailyRes.error;
      if (txSeedRes.error) throw txSeedRes.error;
      if (manualSeedRes.error) throw manualSeedRes.error;

      const accountRows = (accountsRes.data ?? []) as any[];
      const dailyRows = (dailyRes.data ?? []) as any[];
      const txSeedRows = (txSeedRes.data ?? []) as any[];
      const manualSeedRows = (manualSeedRes.data ?? []) as ManualBalanceRow[];

      const accountMeta = new Map<string, BankAccountMeta>();
      for (const row of accountRows) {
        accountMeta.set(String(row.id), {
          id: String(row.id),
          name: row.name ?? "Unnamed account",
          currency: row.currency ?? null,
          exchange_rate_type: row.exchange_rate_type ?? null,
        });
      }

      const byAccount = new Map<string, Map<string, OpeningBalanceRow>>();
      for (const id of bankAccountIds) {
        byAccount.set(id, new Map());
      }

      // ---------- Seed carry balances (last tx / manual strictly before dateFrom) ----------
      const txLastBeforeFrom = new Map<string, TxClosingRow & { dayKey: string | null }>();
      for (const row of txSeedRows) {
        const accId = String(row.bank_account_id);
        const txTs = String(row.transaction_date);
        txLastBeforeFrom.set(accId, {
          bal: toNum(row.end_balance),
          ccy:
            row.end_balance_currency ??
            row.transaction_amount_currency ??
            accountMeta.get(accId)?.currency ??
            null,
          ts: txTs,
          tx_id: null,
          dayKey: txTs.slice(0, 10),
        });
      }

      const manualLastBeforeFrom = new Map<string, ManualClosingRow>();
      for (const row of manualSeedRows) {
        const accId = String(row.bank_account_id);
        manualLastBeforeFrom.set(accId, {
          bal: toNum(row.closing_balance),
          ccy: row.currency ?? accountMeta.get(accId)?.currency ?? null,
          balance_date: String(row.balance_date).slice(0, 10),
          created_at: row.created_at ?? null,
          snapshot_id: row.id ?? null,
        });
      }

      // ---------- Aggregated first/last per (account, day) ----------
      const txOpeningByDay = new Map<string, Map<string, OpeningBalanceRow>>();
      const txClosingByDay = new Map<string, Map<string, TxClosingRow>>();

      for (const row of dailyRows) {
        const accId = String(row.bank_account_id);
        const dayKey = String(row.day).slice(0, 10);
        if (!dateKeySet.has(dayKey)) continue;

        // First tx of the day → derive opening
        const firstEnd = toNum(row.first_end_balance);
        const firstCcy =
          row.first_end_balance_currency ??
          row.first_transaction_amount_currency ??
          accountMeta.get(accId)?.currency ??
          null;
        const firstAmountRaw = toNum(row.first_transaction_amount) ?? 0;
        const firstAmountAbs = Math.abs(firstAmountRaw);
        const firstFee = toNum(row.first_transaction_fee) ?? 0;
        const firstType = normType(row.first_transaction_type);
        const firstSigned =
          firstType === "credit"
            ? firstAmountAbs
            : firstType === "debit"
              ? -firstAmountAbs
              : firstAmountRaw;
        const opening = firstEnd == null ? null : firstEnd - firstSigned + firstFee;

        if (!txOpeningByDay.has(accId)) txOpeningByDay.set(accId, new Map());
        txOpeningByDay.get(accId)!.set(dayKey, {
          opening_balance: opening,
          opening_balance_ts: row.first_ts ?? null,
          currency: firstCcy,
          tx_id: row.first_tx_id ?? null,
        });

        // Last tx of the day → closing
        const lastEnd = toNum(row.last_end_balance);
        const lastCcy =
          row.last_end_balance_currency ??
          row.last_transaction_amount_currency ??
          accountMeta.get(accId)?.currency ??
          null;

        if (!txClosingByDay.has(accId)) txClosingByDay.set(accId, new Map());
        txClosingByDay.get(accId)!.set(dayKey, {
          bal: lastEnd,
          ccy: lastCcy,
          ts: row.last_ts ?? null,
          tx_id: row.last_tx_id ?? null,
        });
      }


      // ---------- Manual balances in range: keep latest record per account/day by created_at ----------
      const manualClosingByDay = new Map<string, Map<string, ManualClosingRow>>();

      for (const row of manualRows) {
        const accId = String(row.bank_account_id);
        const dayKey = String(row.balance_date).slice(0, 10);
        const closing = toNum(row.closing_balance);
        const ccy = row.currency ?? accountMeta.get(accId)?.currency ?? null;

        const candidate: ManualClosingRow = {
          bal: closing,
          ccy,
          balance_date: dayKey,
          created_at: row.created_at ?? null,
          snapshot_id: row.id ?? null,
        };

        if (!manualClosingByDay.has(accId)) {
          manualClosingByDay.set(accId, new Map());
        }
        const dayMap = manualClosingByDay.get(accId)!;
        const existingDay = dayMap.get(dayKey);

        if (
          !existingDay ||
          compareIsoTs(existingDay.created_at, candidate.created_at) < 0
        ) {
          dayMap.set(dayKey, candidate);
        }
      }


      // ---------- Build resolved opening balances ----------
      for (const accId of bankAccountIds) {
        const out = byAccount.get(accId)!;

        const txSeed = txLastBeforeFrom.get(accId);
        const manualSeed = manualLastBeforeFrom.get(accId);

        let carryBal: number | null = null;
        let carryCcy: string | null = accountMeta.get(accId)?.currency ?? null;

        if (txSeed && manualSeed) {
          const byDate = compareIsoDate(txSeed.dayKey, manualSeed.balance_date);

          if (byDate < 0) {
            carryBal = manualSeed.bal;
            carryCcy = manualSeed.ccy ?? carryCcy;
          } else if (byDate > 0) {
            carryBal = txSeed.bal;
            carryCcy = txSeed.ccy ?? carryCcy;
          } else {
            // same business date -> manual wins
            carryBal = manualSeed.bal;
            carryCcy = manualSeed.ccy ?? carryCcy;
          }
        } else if (manualSeed) {
          carryBal = manualSeed.bal;
          carryCcy = manualSeed.ccy ?? carryCcy;
        } else if (txSeed) {
          carryBal = txSeed.bal;
          carryCcy = txSeed.ccy ?? carryCcy;
        }

        const oMap = txOpeningByDay.get(accId) ?? new Map();
        const txCloseMap = txClosingByDay.get(accId) ?? new Map();
        const manualCloseMap = manualClosingByDay.get(accId) ?? new Map();

        for (const day of dateKeys) {
          const txOpening = oMap.get(day);

          if (txOpening) {
            out.set(day, txOpening);
          } else {
            out.set(day, {
              opening_balance: carryBal,
              opening_balance_ts: null,
              currency: carryCcy,
            });
          }

          const manualClosing = manualCloseMap.get(day);
          const txClosing = txCloseMap.get(day);

          // closing(day): manual wins over tx closing on same day
          if (manualClosing && manualClosing.bal != null) {
            carryBal = manualClosing.bal;
            carryCcy = manualClosing.ccy ?? carryCcy;
          } else if (txClosing && txClosing.bal != null) {
            carryBal = txClosing.bal;
            carryCcy = txClosing.ccy ?? carryCcy;
          }
        }
      }

      const consolidatedByCurrency = new Map<string, ConsolidatedOpeningBalancesRow>();

      for (const day of dateKeys) {
        const totals: ConsolidatedOpeningBalancesRow = {};

        for (const accId of bankAccountIds) {
          const row = byAccount.get(accId)?.get(day);
          if (!row) continue;

          const balance = row.opening_balance;
          const currency = row.currency ?? accountMeta.get(accId)?.currency ?? null;

          if (balance == null || !currency) continue;

          totals[currency] = (totals[currency] ?? 0) + balance;
        }

        consolidatedByCurrency.set(day, totals);
      }

      if (!targetCurrency) {
        return {
          dateKeys,
          byAccount,
          consolidatedByCurrency,
          accountMeta,
        };
      }

      // ---------- FX ----------
      const ratesByKey = new Map<string, ExchangeRateRow[]>();

      for (const row of fxRows) {
        const key = buildRateLookupKey({
          currencyPair: row.currency_pair,
          rateType: row.rate_type,
          bankAccountId: row.bank_account_id,
        });

        if (!ratesByKey.has(key)) {
          ratesByKey.set(key, []);
        }
        ratesByKey.get(key)!.push(row);
      }

      const findLatestRateRow = (params: {
        currencyPair: string;
        rateType: string;
        bankAccountId?: string | null;
        date: string;
      }): ExchangeRateRow | null => {
        const key = buildRateLookupKey({
          currencyPair: params.currencyPair,
          rateType: params.rateType,
          bankAccountId: params.bankAccountId,
        });

        const arr = ratesByKey.get(key);
        if (!arr?.length) return null;

        const found = arr.find((r) => r.effective_date <= params.date);
        if (!found) return null;

        const num = toNum(found.rate);
        if (!num || num <= 0) return null;

        return found;
      };

      const STABLECOIN_TO_USD: Record<string, number> = { USDT: 1, USDC: 1 };
      const ETB_PAIR_MAP: Record<string, string> = {
        USD: "USD/ETB",
        EUR: "EUR/ETB",
        GBP: "GBP/ETB",
      };

      const tryDirectOrInverse = (p: {
        sourceCurrency: string;
        targetCurrency: string;
        date: string;
        rateType: string;
        bankAccountId: string | null;
      }): { factor: number; rate: number; rateDate: string } | null => {
        const directPair = `${p.sourceCurrency}/${p.targetCurrency}`;
        const reversePair = `${p.targetCurrency}/${p.sourceCurrency}`;

        const directRow =
          findLatestRateRow({ currencyPair: directPair, rateType: p.rateType, bankAccountId: p.bankAccountId, date: p.date }) ??
          findLatestRateRow({ currencyPair: directPair, rateType: p.rateType, bankAccountId: null, date: p.date });
        if (directRow) {
          const r = toNum(directRow.rate);
          if (r && r > 0) return { factor: r, rate: r, rateDate: directRow.effective_date };
        }

        const reverseRow =
          findLatestRateRow({ currencyPair: reversePair, rateType: p.rateType, bankAccountId: p.bankAccountId, date: p.date }) ??
          findLatestRateRow({ currencyPair: reversePair, rateType: p.rateType, bankAccountId: null, date: p.date });
        if (reverseRow) {
          const r = toNum(reverseRow.rate);
          if (r && r > 0) return { factor: 1 / r, rate: r, rateDate: reverseRow.effective_date };
        }

        return null;
      };

      // Cross-currency routing via ETB (e.g. EUR→USD with only EUR/ETB and USD/ETB)
      const tryEtbRouted = (p: {
        sourceCurrency: string;
        targetCurrency: string;
        date: string;
        rateType: string;
        bankAccountId: string | null;
      }): { factor: number; rate: number; rateDate: string } | null => {
        if (p.sourceCurrency === "ETB" || p.targetCurrency === "ETB") return null;
        const srcPair = ETB_PAIR_MAP[p.sourceCurrency];
        const tgtPair = ETB_PAIR_MAP[p.targetCurrency];
        if (!srcPair || !tgtPair) return null;

        const srcRow =
          findLatestRateRow({ currencyPair: srcPair, rateType: p.rateType, bankAccountId: p.bankAccountId, date: p.date }) ??
          findLatestRateRow({ currencyPair: srcPair, rateType: p.rateType, bankAccountId: null, date: p.date });
        const tgtRow =
          findLatestRateRow({ currencyPair: tgtPair, rateType: p.rateType, bankAccountId: p.bankAccountId, date: p.date }) ??
          findLatestRateRow({ currencyPair: tgtPair, rateType: p.rateType, bankAccountId: null, date: p.date });

        const sR = srcRow ? toNum(srcRow.rate) : null;
        const tR = tgtRow ? toNum(tgtRow.rate) : null;
        if (!sR || sR <= 0 || !tR || tR <= 0) return null;

        const factor = sR / tR;
        const rateDate =
          srcRow!.effective_date <= tgtRow!.effective_date
            ? srcRow!.effective_date
            : tgtRow!.effective_date;
        return { factor, rate: factor, rateDate };
      };

      const convertAmountDetailed = (params: {
        amount: number;
        sourceCurrency: string;
        targetCurrency: DisplayCurrency;
        date: string;
        rateType?: string | null;
        bankAccountId?: string | null;
      }): {
        convertedAmount: number | null;
        appliedRate: number | null;
        rateDate: string | null;
        rateType: string | null;
      } => {
        const { amount, sourceCurrency, targetCurrency, date, bankAccountId = null } = params;
        const rateType =
          (params.rateType && String(params.rateType).trim()) || "bank_rate";

        if (!Number.isFinite(amount) || !sourceCurrency) {
          return { convertedAmount: null, appliedRate: null, rateDate: null, rateType };
        }

        if (sourceCurrency === targetCurrency) {
          return { convertedAmount: amount, appliedRate: 1, rateDate: date, rateType };
        }

        // Stablecoins → pin to USD, then route to target if needed
        if (STABLECOIN_TO_USD[sourceCurrency] != null) {
          const usd = amount * STABLECOIN_TO_USD[sourceCurrency];
          if (targetCurrency === "USD") {
            return { convertedAmount: usd, appliedRate: 1, rateDate: date, rateType };
          }
          const routed =
            tryDirectOrInverse({ sourceCurrency: "USD", targetCurrency, date, rateType, bankAccountId }) ??
            tryDirectOrInverse({ sourceCurrency: "USD", targetCurrency, date, rateType: "bank_rate", bankAccountId });
          if (routed) {
            return {
              convertedAmount: usd * routed.factor,
              appliedRate: routed.rate,
              rateDate: routed.rateDate,
              rateType,
            };
          }
          return { convertedAmount: null, appliedRate: null, rateDate: null, rateType };
        }

        // 1) Direct/inverse with configured rate type
        const a = tryDirectOrInverse({ sourceCurrency, targetCurrency, date, rateType, bankAccountId });
        if (a) return { convertedAmount: amount * a.factor, appliedRate: a.rate, rateDate: a.rateDate, rateType };

        // 2) ETB-routed with configured rate type
        const b = tryEtbRouted({ sourceCurrency, targetCurrency, date, rateType, bankAccountId });
        if (b) return { convertedAmount: amount * b.factor, appliedRate: b.rate, rateDate: b.rateDate, rateType };

        // 3) Last-resort fallback to bank_rate
        if (rateType !== "bank_rate") {
          const c = tryDirectOrInverse({ sourceCurrency, targetCurrency, date, rateType: "bank_rate", bankAccountId });
          if (c) return { convertedAmount: amount * c.factor, appliedRate: c.rate, rateDate: c.rateDate, rateType: "bank_rate" };
          const d = tryEtbRouted({ sourceCurrency, targetCurrency, date, rateType: "bank_rate", bankAccountId });
          if (d) return { convertedAmount: amount * d.factor, appliedRate: d.rate, rateDate: d.rateDate, rateType: "bank_rate" };
        }

        return { convertedAmount: null, appliedRate: null, rateDate: null, rateType };
      };

      const converted = new Map<string, ConvertedOpeningBalanceDay>();

      for (const day of dateKeys) {
        let total = 0;

        const includedAccountIds: string[] = [];
        const missingFxAccountIds: string[] = [];
        const missingSourceAccountIds: string[] = [];

        const includedAccounts: AccountBalanceDetail[] = [];
        const missingFxAccounts: AccountBalanceDetail[] = [];
        const missingSourceAccounts: AccountBalanceDetail[] = [];

        const missingFxAmountByCurrency: Record<string, number> = {};
        const missingSourceAmountByCurrency: Record<string, number> = {};

        for (const accId of bankAccountIds) {
          const row = byAccount.get(accId)?.get(day);
          const meta = accountMeta.get(accId);

          const accountName = meta?.name ?? "Unnamed account";
          const accountCurrency = row?.currency ?? meta?.currency ?? null;
          const opening = row?.opening_balance ?? null;
          const rateType = meta?.exchange_rate_type ?? "bank_rate";

          if (!row) {
            missingSourceAccountIds.push(accId);
            missingSourceAccounts.push({
              accountId: accId,
              accountName,
              currency: accountCurrency,
              amount: null,
              reason: "missing_source",
              rateType,
            });
            continue;
          }

          if (opening == null || !accountCurrency) {
            missingSourceAccountIds.push(accId);
            addToCurrencyBucket(missingSourceAmountByCurrency, accountCurrency, opening);

            missingSourceAccounts.push({
              accountId: accId,
              accountName,
              currency: accountCurrency,
              amount: opening,
              reason: "missing_source",
              rateType,
            });
            continue;
          }

          const conversion = convertAmountDetailed({
            amount: opening,
            sourceCurrency: accountCurrency,
            targetCurrency,
            date: day,
            rateType,
            bankAccountId: accId,
          });

          if (conversion.convertedAmount == null) {
            missingFxAccountIds.push(accId);
            addToCurrencyBucket(missingFxAmountByCurrency, accountCurrency, opening);

            missingFxAccounts.push({
              accountId: accId,
              accountName,
              currency: accountCurrency,
              amount: opening,
              reason: "missing_fx",
              rateType,
              appliedRate: null,
              convertedAmount: null,
              targetCurrency,
              rateDate: null,
            });
            continue;
          }

          total += conversion.convertedAmount;
          includedAccountIds.push(accId);
          includedAccounts.push({
            accountId: accId,
            accountName,
            currency: accountCurrency,
            amount: opening,
            reason: "included",
            rateType,
            appliedRate: conversion.appliedRate,
            convertedAmount: conversion.convertedAmount,
            targetCurrency,
            rateDate: conversion.rateDate,
          });
        }

        converted.set(day, {
          amount: total,
          currency: targetCurrency,
          isPartial:
            missingFxAccountIds.length > 0 || missingSourceAccountIds.length > 0,

          includedAccountIds,
          includedAccounts,

          missingFxAccountIds,
          missingFxAccounts,
          missingFxAccountsCount: missingFxAccountIds.length,
          missingFxAmountByCurrency,

          missingSourceAccountIds,
          missingSourceAccounts,
          missingSourceAccountsCount: missingSourceAccountIds.length,
          missingSourceAmountByCurrency,
        });
      }

      // Debug: surface per-account breakdown for the first day (period opening)
      // so we can reconcile against finance/banking/balances.
      if (typeof window !== "undefined" && converted.size > 0 && dateKeys.length > 0) {
        const firstDay = dateKeys[0];
        const day = converted.get(firstDay);
        if (day) {
          // eslint-disable-next-line no-console
          console.debug(
            `[useStartingBalance] Opening breakdown for ${firstDay} (target=${targetCurrency}):`,
            {
              total: day.amount,
              included: day.includedAccounts.map((a) => ({
                name: a.accountName,
                ccy: a.currency,
                native: a.amount,
                rateType: a.rateType,
                rate: a.appliedRate,
                rateDate: a.rateDate,
                converted: a.convertedAmount,
              })),
              missingFx: day.missingFxAccounts.map((a) => ({
                name: a.accountName,
                ccy: a.currency,
                native: a.amount,
                rateType: a.rateType,
              })),
              missingSource: day.missingSourceAccounts.map((a) => ({
                name: a.accountName,
                ccy: a.currency,
              })),
            }
          );
        }
      }

      return {
        dateKeys,
        byAccount,
        consolidatedByCurrency,
        accountMeta,
        converted,
      };

    },

    staleTime: 15_000,
  });
}